import { useEffect, useRef, useState } from "react";
import { ArrowRight, ExternalLink, Youtube } from "lucide-react";
import api, { imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import PartnerModal from "@/components/PartnerModal";
import { scrollToSection } from "@/lib/smoothScroll";
import { useLang } from "@/context/LanguageContext";

const monogram = (name) => name.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

function CollabChip({ item, onOpen, testid }) {
  const { t } = useLang();
  const photo = item.photo_url ? imgSrc(item.photo_url, 200) : null;
  return (
    <div className="relative flex items-center gap-3 sm:gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-md pl-3.5 pr-3 sm:pl-5 sm:pr-4 py-2.5 sm:py-3.5 transition-colors duration-300 hover:border-amber-400/60">
      <button
        data-testid={testid}
        onClick={() => onOpen({
          name: item.company,
          title: item.project_title,
          tagline: item.year ? t("collab.taglineYear", { year: item.year }) : t("collab.tagline"),
          logo_url: item.logo_url,
          cover: item.photo_url,
          intro: item.note,
          project: item.project_desc,
          experience: item.experience,
          info: { year: item.year, location: item.location, type: item.project_type },
          photos: item.photos || [],
          video_url: item.video_url,
          link: item.link,
        })}
        className="flex min-w-0 flex-1 items-center gap-4 text-left focus:outline-none"
      >
        {item.logo_url ? (
          <img src={imgSrc(item.logo_url)} alt={`Logo ${item.company}`} className="h-9 w-auto min-w-9 max-w-28 sm:h-11 sm:min-w-11 sm:max-w-36 shrink-0 rounded-lg bg-white object-contain px-1.5 py-1 sm:px-2 sm:py-1.5" />
        ) : (
          <span className="flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-lg border border-slate-700 bg-[#0C1220] font-display text-base sm:text-lg text-slate-300">
            {monogram(item.company)}
          </span>
        )}
        <span className="min-w-0 text-left">
          <span className="block text-xs sm:text-sm font-bold uppercase tracking-[0.12em] text-slate-100 whitespace-nowrap">{item.company}</span>
          {item.project_title && <span className="block text-[9px] sm:text-[10px] text-sky-300/90 whitespace-nowrap">{item.project_title}</span>}
        </span>
        {photo && (
          <img src={photo} alt={item.project_title || item.company} loading="lazy" className="hidden sm:block h-12 w-20 shrink-0 rounded-lg border border-slate-700/70 object-cover" />
        )}
      </button>
      {item.video_url && (
        <a
          href={item.video_url}
          target="_blank"
          rel="noreferrer"
          data-testid={testid ? `${testid}-video` : undefined}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Video YouTube: ${item.company}`}
          className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full border border-red-500/70 bg-red-500/15 text-red-500 shadow-[0_0_12px_rgba(239,68,68,0.25)] transition-colors duration-300 hover:bg-red-500 hover:text-white"
        >
          <Youtube className="h-4 w-4" />
        </a>
      )}
      {item.link && (
        <a
          href={item.link}
          target="_blank"
          rel="noreferrer"
          data-testid={testid ? `${testid}-site` : undefined}
          onClick={(e) => e.stopPropagation()}
          aria-label={`${t("modal.visit")}: ${item.company}`}
          className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full border border-slate-700 text-slate-400 transition-colors duration-300 hover:border-sky-400/60 hover:text-sky-300"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function MarqueeRow({ items, reverse, onOpen, showIds }) {
  const trackRef = useRef(null);

  // Mobile/tablet: dito sulla riga = scorrimento in pausa, al rilascio riparte.
  // Così si può leggere e toccare un chip senza che la riga scappi via.
  const onTouchStart = () => trackRef.current?.classList.add("marquee-touch-pause");
  const onTouchEnd = () => trackRef.current?.classList.remove("marquee-touch-pause");

  if (!items.length) return null;
  // Ripete i chip fino a riempire sempre tutta la larghezza: niente spazi vuoti ai lati
  const unit = [];
  while (unit.length < Math.max(8, items.length * 2)) unit.push(...items);
  const row = [...unit, ...unit];
  return (
    <div
      className={`marquee-hover-pause relative overflow-hidden py-2 ${reverse ? "mt-1" : ""}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <span className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 sm:w-28 bg-gradient-to-r from-[#0B101B] to-transparent" />
      <span className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 sm:w-28 bg-gradient-to-l from-[#0B101B] to-transparent" />
      <div ref={trackRef} className={`flex w-max items-center gap-4 pr-4 ${reverse ? "animate-marquee-reverse" : "animate-marquee"}`}>
        {row.map((item, i) => (
          <CollabChip
            key={`${item.id}-${i}`}
            item={item}
            onOpen={onOpen}
            testid={showIds && i < items.length ? `collab-chip-${item.id}` : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default function Collaborazioni() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/collaborations").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items !== null && items.length === 0) return null;

  const half = items ? Math.ceil(items.length / 2) : 0;
  const rowA = items ? items.slice(0, half) : [];
  const rowB = items ? items.slice(half) : [];

  return (
    <section id="collaborazioni" className="py-16 sm:py-20 bg-[#0B101B] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("collab.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("collab.titleA")}<span className="text-amber-400">{t("collab.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            {t("collab.subtitle")}
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-8">
        <div data-testid="collab-marquee">
          <MarqueeRow items={rowA} onOpen={setSelected} showIds />
          <MarqueeRow items={rowB} reverse onOpen={setSelected} />
        </div>
      </Reveal>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal delay={0.15} className="mt-12">
          <div className="relative overflow-hidden rounded-2xl border border-amber-400/25 bg-gradient-to-br from-slate-900/80 to-[#0C1220] p-8 sm:p-10">
            <span className="font-display text-outline pointer-events-none absolute -bottom-6 -right-2 text-[10rem] leading-none select-none hidden lg:block">{t("collab.outline")}</span>
            <h3 className="font-display text-3xl sm:text-4xl text-slate-50 relative">
              {t("collab.ctaTitleA")}<span className="text-amber-400">{t("collab.ctaTitleAccent")}</span>
            </h3>
            <p className="mt-3 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed relative">
              {t("collab.ctaText")}
            </p>
            <button
              data-testid="collab-cta-contact"
              onClick={() => scrollToSection("#contatti")}
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-[#090D14] transition-transform duration-300 hover:scale-[1.03] relative"
            >
              {t("collab.ctaBtn")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>

      <PartnerModal item={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
